import {
    createCartItemUrl,
    getCartDrawerCleanUrl,
    getDrawerDragOffset,
    getNextQuantity,
    normalizeCartPayload,
    shouldCloseFromSwipe,
    shouldOpenCartFromClick,
} from './cart-utils.mjs';
import { initCartSharing } from './site-cart-share.js';

const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container) =>
    [...container.querySelectorAll(focusableSelector)].filter(
        (element) => !element.closest('[inert]') && element.getClientRects().length > 0,
    );

const setInert = (element, inert) => {
    element.inert = inert;

    if (inert) {
        element.setAttribute('inert', '');
    } else {
        element.removeAttribute('inert');
    }
};

const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);

    if (className) {
        element.className = className;
    }

    if (text !== undefined && text !== null) {
        element.textContent = String(text);
    }

    return element;
};

const toSafeUrl = (value, fallback = '#') => {
    if (!value) {
        return fallback;
    }

    try {
        const url = new URL(String(value || ''), window.location.origin);

        if (['http:', 'https:'].includes(url.protocol)) {
            return url.toString();
        }
    } catch {
        // Invalid API URLs remain inert.
    }

    return fallback;
};

const parseInitialPayload = (drawer) => {
    const node = drawer.querySelector('[data-cart-initial]');

    if (!node?.textContent) {
        return null;
    }

    try {
        return normalizeCartPayload(JSON.parse(node.textContent));
    } catch {
        return null;
    }
};

const createCartItem = (item, index) => {
    const key = String(item?.key || '');
    const article = createElement('article', 'CartDrawerItem');
    article.dataset.cartItemKey = key;
    article.style.setProperty('--cart-item-index', String(index));
    article.dataset.minimumQuantity = String(item?.minimum_quantity || 1);
    article.dataset.maximumQuantity = String(item?.maximum_quantity || 999);

    const media = createElement('a', 'CartDrawerItem__media');
    media.href = toSafeUrl(item?.url);
    media.setAttribute('aria-label', `Zobacz produkt: ${String(item?.name || 'Produkt')}`);

    if (item?.image?.src) {
        const image = createElement('img', 'CartDrawerItem__image');
        image.src = toSafeUrl(item.image.src, '');
        image.alt = String(item?.image?.alt || item?.name || '');
        image.loading = index < 2 ? 'eager' : 'lazy';
        image.decoding = 'async';
        image.width = Number.parseInt(item?.image?.width, 10) || 300;
        image.height = Number.parseInt(item?.image?.height, 10) || 300;

        if (item?.image?.srcset) {
            image.srcset = String(item.image.srcset);
            image.sizes = String(item?.image?.sizes || '(max-width: 480px) 92px, 112px');
        }

        image.addEventListener('error', () => {
            image.remove();
            media.classList.add('is-placeholder');
        }, { once: true });
        media.append(image);
    } else {
        media.classList.add('is-placeholder');
    }

    const details = createElement('div', 'CartDrawerItem__details');
    details.append(createElement('p', 'CartDrawerItem__category', item?.category || 'Produkt'));

    const title = createElement('a', 'CartDrawerItem__title', item?.name || 'Produkt');
    title.href = toSafeUrl(item?.url);
    details.append(title);

    if (item?.variation) {
        details.append(createElement('p', 'CartDrawerItem__variation', item.variation));
    }

    if (item?.bundle_offer?.label) {
        details.append(createElement('p', 'CartDrawerItem__bundleOffer', item.bundle_offer.label));
    }

    if (item?.bogo_offer?.label) {
        details.append(createElement('p', 'CartDrawerItem__bogoOffer', item.bogo_offer.label));
    }

    const actions = createElement('div', 'CartDrawerItem__actions');
    const pricing = item?.pricing && typeof item.pricing === 'object' ? item.pricing : {};
    const currentPrice = String(pricing.current || item?.price || '');
    const regularPrice = String(pricing.regular || '');
    const discounted = Boolean(pricing.discounted && regularPrice);
    const price = createElement(
        'p',
        `CartDrawerItem__price${discounted ? ' is-discounted' : ''}`,
    );
    price.append(createElement('span', 'CartDrawerItem__priceCurrent', currentPrice));

    if (discounted) {
        price.append(createElement('del', 'CartDrawerItem__priceRegular', regularPrice));
    }

    actions.append(price);

    const quantity = createElement('div', 'CartDrawerItem__quantity');
    quantity.setAttribute('aria-label', `Ilość produktu ${String(item?.name || '')}`);

    const decrease = createElement('button', '', '−');
    decrease.type = 'button';
    decrease.dataset.cartDecrease = key;
    decrease.disabled = item?.can_decrease === false;
    decrease.setAttribute('aria-label', `Zmniejsz ilość: ${String(item?.name || 'produkt')}`);

    const output = createElement('output', '', item?.quantity || 1);
    output.dataset.cartQuantity = key;
    output.setAttribute('aria-live', 'polite');

    const increase = createElement('button', '', '+');
    increase.type = 'button';
    increase.dataset.cartIncrease = key;
    increase.disabled = item?.can_increase === false;
    increase.setAttribute('aria-label', `Zwiększ ilość: ${String(item?.name || 'produkt')}`);

    quantity.append(decrease, output, increase);
    actions.append(quantity);

    const remove = createElement('button', 'CartDrawerItem__remove', 'Usuń');
    remove.type = 'button';
    remove.dataset.cartRemove = key;
    remove.setAttribute('aria-label', `Usuń z koszyka: ${String(item?.name || 'produkt')}`);
    actions.append(remove);

    details.append(actions);
    article.append(media, details);

    return article;
};

const createSummaryRow = (row) => {
    const tone = ['muted', 'accent', 'saving'].includes(row?.tone)
        ? ` CartDrawer__summaryRow--${row.tone}`
        : '';
    const element = createElement('div', `CartDrawer__summaryRow${tone}`);
    element.dataset.cartSummaryRow = String(row?.key || 'row');
    element.append(
        createElement('span', '', row?.label || ''),
        createElement('span', '', row?.value || ''),
    );

    return element;
};

class CartRequestError extends Error {
    constructor(message, status = 0, code = '') {
        super(message);
        this.name = 'CartRequestError';
        this.status = status;
        this.code = code;
    }
}

export const initSiteCart = (header, { beforeOpen, onAdded } = {}) => {
    const drawer = header.querySelector('[data-cart-drawer]');
    const surface = drawer?.querySelector('[data-cart-surface]');
    const toggles = [...header.querySelectorAll('[data-cart-toggle]')];

    if (!drawer || !surface || !toggles.length || drawer.dataset.cartReady === 'true') {
        return null;
    }

    const closeButtons = [...drawer.querySelectorAll('[data-cart-close]')];
    const body = drawer.querySelector('[data-cart-scroll]');
    const shipping = drawer.querySelector('[data-cart-shipping]');
    const shippingMessage = drawer.querySelector('[data-cart-shipping-message]');
    const shippingCaption = drawer.querySelector('[data-cart-shipping-caption]');
    const shippingProgress = drawer.querySelector('[data-cart-shipping-progress]');
    const loading = drawer.querySelector('[data-cart-loading]');
    const empty = drawer.querySelector('[data-cart-empty]');
    const contents = drawer.querySelector('[data-cart-contents]');
    const items = drawer.querySelector('[data-cart-items]');
    const summary = drawer.querySelector('[data-cart-summary]');
    const summaryRows = drawer.querySelector('[data-cart-summary-rows]');
    const total = drawer.querySelector('[data-cart-total]');
    const checkout = drawer.querySelector('[data-cart-checkout]');
    const errorBanner = drawer.querySelector('[data-cart-error]');
    const errorMessage = drawer.querySelector('[data-cart-error-message]');
    const toast = drawer.querySelector('[data-cart-toast]');
    const toastMessage = drawer.querySelector('[data-cart-toast-message]');
    const toastAction = drawer.querySelector('[data-cart-toast-action]');
    const liveRegion = drawer.querySelector('[data-cart-live]');
    const countLabels = [...drawer.querySelectorAll('[data-cart-count-label]')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const endpoint = drawer.dataset.cartEndpoint || '';
    const desiredQuantities = new Map();
    const quantityTimers = new Map();
    const syncingKeys = new Set();
    let snapshot = null;
    let hasSnapshot = false;
    let returnFocus = null;
    let refreshController = null;
    let refreshSequence = 0;
    let closeTimer = 0;
    let focusTimer = 0;
    let toastTimer = 0;
    let toastCallback = null;
    let lastRefresh = 0;
    let mutationQueue = Promise.resolve();
    let suppressWooRefreshUntil = 0;
    let drag = null;

    const announce = (message) => {
        if (!liveRegion) {
            return;
        }

        liveRegion.textContent = '';
        window.setTimeout(() => {
            liveRegion.textContent = String(message || '');
        }, 20);
    };

    const setBusy = (busy, refreshing = false) => {
        drawer.setAttribute('aria-busy', String(busy));
        drawer.classList.toggle('is-refreshing', busy && refreshing);
    };

    const updateHeaderBadge = (payload) => {
        const count = Math.max(0, Number.parseInt(payload?.count, 10) || 0);
        const display = count > 99 ? '99+' : String(count);
        const label = `Liczba produktów w koszyku: ${count}`;

        document.querySelectorAll('.SiteHeader__cartBadge').forEach((badge) => {
            badge.setAttribute('aria-label', label);
            const value = badge.querySelector('[aria-hidden="true"]');

            if (value) {
                value.textContent = display;
            }
        });
    };

    const hideError = () => {
        if (errorBanner) {
            errorBanner.hidden = true;
        }
    };

    const showError = (message, { full = false } = {}) => {
        if (errorBanner && errorMessage) {
            errorMessage.textContent = String(message || 'Nie udało się zaktualizować koszyka.');
            errorBanner.hidden = false;
        }

        if (full) {
            loading.hidden = true;
            empty.hidden = true;
            contents.hidden = true;
            summary.hidden = true;
            drawer.dataset.state = 'error';
        }

        setBusy(false);
        announce(message);
    };

    const showToast = (message, action = null) => {
        window.clearTimeout(toastTimer);
        toastCallback = action?.callback || null;
        toastMessage.textContent = String(message || '');
        toastAction.hidden = !action;
        toastAction.textContent = action?.label || '';
        toast.hidden = false;
        window.requestAnimationFrame(() => toast.classList.add('is-visible'));
        toastTimer = window.setTimeout(() => {
            toast.classList.remove('is-visible');
            window.setTimeout(() => {
                toast.hidden = true;
                toastCallback = null;
            }, reducedMotion.matches ? 0 : 280);
        }, action ? 6500 : 4200);
    };

    const updateSummaryHeight = () => {
        const height = summary && !summary.hidden ? summary.offsetHeight : 0;
        drawer.style.setProperty('--cart-summary-height', `${height}px`);
    };

    const applyPendingQuantities = () => {
        desiredQuantities.forEach((quantity, key) => {
            const item = items.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);

            if (!item) {
                return;
            }

            const minimum = Number.parseInt(item.dataset.minimumQuantity, 10) || 1;
            const maximum = Number.parseInt(item.dataset.maximumQuantity, 10) || 999;
            const output = item.querySelector('[data-cart-quantity]');
            const decrease = item.querySelector('[data-cart-decrease]');
            const increase = item.querySelector('[data-cart-increase]');

            if (output) {
                output.textContent = String(quantity);
            }

            if (decrease) {
                decrease.disabled = quantity <= minimum;
            }

            if (increase) {
                increase.disabled = quantity >= maximum;
            }

            item.classList.add('is-updating');
        });
    };

    const renderSnapshot = (rawPayload, { announceUpdate = false } = {}) => {
        const payload = normalizeCartPayload(rawPayload);
        snapshot = payload;
        hasSnapshot = payload.status === 'ready';
        lastRefresh = Date.now();
        drawer.dataset.state = payload.is_empty ? 'empty' : 'ready';
        drawer.classList.toggle('is-empty', payload.is_empty);
        drawer.dataset.cartNonce = payload.nonce || drawer.dataset.cartNonce || '';
        drawer.dataset.restNonce = payload.rest_nonce || drawer.dataset.restNonce || '';
        hideError();
        setBusy(false);
        loading.hidden = true;

        countLabels.forEach((element) => {
            element.textContent = payload.count_label;
        });
        updateHeaderBadge(payload);

        if (shipping) {
            const progress = payload.shipping_progress;
            shipping.hidden = payload.is_empty || !progress.enabled;
            shipping.classList.toggle('is-complete', progress.complete);
            if (progress.complete) {
                shippingMessage.replaceChildren(
                    createElement('span', 'CartDrawer__shippingLead', 'Otrzymujesz '),
                    createElement('strong', 'CartDrawer__shippingBenefit', 'darmową dostawę!'),
                );
            } else {
                shippingMessage.textContent = progress.message;
            }
            shippingCaption.textContent = progress.complete ? '' : progress.caption;
            shippingCaption.hidden = progress.complete;
            shipping.style.setProperty('--cart-shipping-progress', `${progress.percent}%`);
            shippingProgress.setAttribute('aria-valuenow', String(Math.round(progress.percent)));
            shippingProgress.setAttribute('aria-valuetext', progress.message);
            shippingProgress.hidden = progress.complete;
        }

        empty.hidden = !payload.is_empty;
        contents.hidden = payload.is_empty;
        summary.hidden = payload.is_empty;
        const shareButton = drawer.querySelector('[data-cart-share]');
        if (shareButton) shareButton.hidden = payload.is_empty;

        if (!payload.is_empty) {
            items.replaceChildren(...payload.items.map(createCartItem));
            summaryRows.replaceChildren(...payload.summary.rows.map(createSummaryRow));
            if (payload.promotion_choice?.coupon_active && payload.promotion_choice?.message) {
                summaryRows.append(createElement('p', 'CartDrawer__promotionChoice', payload.promotion_choice.message));
            }
            total.textContent = payload.summary.total;
            checkout.href = toSafeUrl(payload.checkout_url, toSafeUrl(payload.cart_url));
        } else {
            items.replaceChildren();
        }

        applyPendingQuantities();
        window.requestAnimationFrame(updateSummaryHeight);

        document.dispatchEvent(new CustomEvent('seluvia:cart', {
            detail: { type: 'updated', payload },
        }));

        if (announceUpdate) {
            announce(`Koszyk zaktualizowany. ${payload.count_label}.`);
        }
    };

    const renderLoading = () => {
        drawer.dataset.state = 'loading';
        hideError();
        setBusy(true, hasSnapshot);

        if (!hasSnapshot) {
            loading.hidden = false;
            empty.hidden = true;
            contents.hidden = true;
            summary.hidden = true;
        }
    };

    const request = async (url, { method = 'GET', body: requestBody, signal, retryNonce = true } = {}) => {
        const controller = new AbortController();
        let timedOut = false;
        const timeout = window.setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, 12000);
        const abort = () => controller.abort();
        signal?.addEventListener('abort', abort, { once: true });
        const headers = { Accept: 'application/json' };

        if (drawer.dataset.restNonce) {
            headers['X-WP-Nonce'] = drawer.dataset.restNonce;
        }

        if (method !== 'GET') {
            headers['Content-Type'] = 'application/json';
            headers['X-Seluvia-Cart-Nonce'] = drawer.dataset.cartNonce || '';
        }

        try {
            const response = await fetch(url, {
                method,
                headers,
                credentials: 'same-origin',
                cache: 'no-store',
                body: requestBody === undefined ? undefined : JSON.stringify(requestBody),
                signal: controller.signal,
            });
            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (response.status === 403 && method !== 'GET' && retryNonce) {
                    const refreshed = await request(endpoint, { method: 'GET', retryNonce: false });
                    renderSnapshot(refreshed);
                    return request(url, { method, body: requestBody, retryNonce: false });
                }

                throw new CartRequestError(
                    payload?.message || `HTTP ${response.status}`,
                    response.status,
                    payload?.code || '',
                );
            }

            return normalizeCartPayload(payload);
        } catch (requestError) {
            if (timedOut) {
                throw new CartRequestError('Koszyk odpowiada zbyt długo. Spróbuj ponownie.');
            }

            throw requestError;
        } finally {
            window.clearTimeout(timeout);
            signal?.removeEventListener('abort', abort);
        }
    };

    const refresh = async ({ force = false, silent = false } = {}) => {
        if (!endpoint || (!force && Date.now() - lastRefresh < 8000)) {
            return snapshot;
        }

        refreshController?.abort();
        refreshController = new AbortController();
        const sequence = ++refreshSequence;

        if (!silent) {
            renderLoading();
        } else {
            setBusy(true, hasSnapshot);
        }

        try {
            const payload = await request(endpoint, { signal: refreshController.signal });

            if (sequence !== refreshSequence) {
                return snapshot;
            }

            renderSnapshot(payload);
            return payload;
        } catch (requestError) {
            if (requestError?.name === 'AbortError' || sequence !== refreshSequence) {
                return snapshot;
            }

            showError(
                requestError?.message || 'Nie udało się pobrać koszyka. Spróbuj ponownie.',
                { full: !hasSnapshot },
            );
            return snapshot;
        }
    };

    const isOpen = () => drawer.classList.contains('is-open');

    const open = ({ refreshCart = true } = {}) => {
        if (isOpen()) {
            surface.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
            if (refreshCart) {
                refresh({ silent: hasSnapshot });
            }
            return;
        }

        const requestedReturnFocus = document.activeElement;
        beforeOpen?.();
        window.clearTimeout(closeTimer);
        window.clearTimeout(focusTimer);
        returnFocus = requestedReturnFocus instanceof HTMLElement
            && !requestedReturnFocus.closest('[aria-hidden="true"], [inert]')
            ? requestedReturnFocus
            : toggles[0];
        setInert(drawer, false);
        drawer.setAttribute('aria-hidden', 'false');
        toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
        document.body.style.setProperty(
            '--seluvia-cart-scrollbar-width',
            `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`,
        );
        document.body.classList.add('has-site-cart-open');
        window.requestAnimationFrame(() => drawer.classList.add('is-open'));
        focusTimer = window.setTimeout(
            () => surface.querySelector('[data-cart-close]')?.focus({ preventScroll: true }),
            reducedMotion.matches ? 0 : 220,
        );

        if (refreshCart) {
            refresh({ silent: hasSnapshot });
        }

        document.dispatchEvent(new CustomEvent('seluvia:cart', { detail: { type: 'open' } }));
    };

    const close = ({ restoreFocus = true } = {}) => {
        if (!isOpen() && drawer.getAttribute('aria-hidden') === 'true') {
            return;
        }

        window.clearTimeout(focusTimer);
        refreshController?.abort();
        refreshSequence += 1;
        drag = null;
        drawer.classList.remove('is-dragging', 'is-open');
        drawer.style.removeProperty('--cart-drawer-drag-x');
        drawer.setAttribute('aria-hidden', 'true');
        toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
        document.body.classList.remove('has-site-cart-open');
        document.body.style.removeProperty('--seluvia-cart-scrollbar-width');
        closeTimer = window.setTimeout(
            () => setInert(drawer, true),
            reducedMotion.matches ? 0 : 520,
        );

        if (restoreFocus && returnFocus instanceof HTMLElement) {
            returnFocus.focus({ preventScroll: true });
        }

        document.dispatchEvent(new CustomEvent('seluvia:cart', { detail: { type: 'close' } }));
    };

    const enqueueMutation = (callback) => {
        mutationQueue = mutationQueue.catch(() => undefined).then(callback);
        return mutationQueue;
    };

    const refreshWooFragments = () => {
        if (!window.jQuery) {
            return;
        }

        suppressWooRefreshUntil = Date.now() + 1500;
        window.jQuery(document.body).trigger('wc_fragment_refresh');
    };

    const presentPostAddOffer = (payload, detail = {}) => {
        if (typeof onAdded !== 'function') {
            return false;
        }

        try {
            return onAdded({ payload, detail }) === true;
        } catch {
            return false;
        }
    };

    const addItem = (bodyData) => enqueueMutation(async () => {
        const payload = await request(`${endpoint.replace(/\/$/, '')}/items`, {
            method: 'POST',
            body: bodyData,
        });
        renderSnapshot(payload, { announceUpdate: true });
        refreshWooFragments();

        return payload;
    });

    const syncQuantity = async (key) => {
        if (syncingKeys.has(key)) {
            return;
        }

        syncingKeys.add(key);

        try {
            while (desiredQuantities.has(key)) {
                const quantity = desiredQuantities.get(key);
                desiredQuantities.delete(key);

                try {
                    const payload = await request(
                        createCartItemUrl(endpoint, key, '', window.location.href),
                        { method: 'POST', body: { quantity } },
                    );
                    renderSnapshot(payload, { announceUpdate: true });
                    refreshWooFragments();
                } catch (requestError) {
                    desiredQuantities.delete(key);

                    if (snapshot) {
                        renderSnapshot(snapshot);
                    }

                    showToast(requestError?.message || 'Nie udało się zmienić ilości.');
                    announce(requestError?.message || 'Nie udało się zmienić ilości.');
                }
            }
        } finally {
            syncingKeys.delete(key);
            items.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`)?.classList.remove('is-updating');
        }
    };

    const queueQuantityChange = (key, delta) => {
        const item = items.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);

        if (!item) {
            return;
        }

        const minimum = Number.parseInt(item.dataset.minimumQuantity, 10) || 1;
        const maximum = Number.parseInt(item.dataset.maximumQuantity, 10) || 999;
        const output = item.querySelector('[data-cart-quantity]');
        const current = desiredQuantities.get(key) ?? Number.parseInt(output?.textContent, 10) ?? minimum;
        const next = getNextQuantity(current, delta, minimum, maximum);

        if (next === current) {
            return;
        }

        desiredQuantities.set(key, next);
        applyPendingQuantities();
        window.clearTimeout(quantityTimers.get(key));
        quantityTimers.set(key, window.setTimeout(() => {
            quantityTimers.delete(key);
            enqueueMutation(() => syncQuantity(key));
        }, 180));
    };

    const removeItem = (key) => {
        const item = items.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
        const productName = item?.querySelector('.CartDrawerItem__title')?.textContent || 'Produkt';
        window.clearTimeout(quantityTimers.get(key));
        quantityTimers.delete(key);
        desiredQuantities.delete(key);
        item?.classList.add('is-removing');

        window.setTimeout(() => {
            enqueueMutation(async () => {
                try {
                    const payload = await request(
                        createCartItemUrl(endpoint, key, '', window.location.href),
                        { method: 'DELETE' },
                    );
                    const undo = payload.undo;
                    renderSnapshot(payload, { announceUpdate: true });
                    refreshWooFragments();
                    showToast(`Usunięto „${productName}”.`, undo?.key ? {
                        label: undo.label || 'Cofnij',
                        callback: () => restoreItem(undo.key),
                    } : null);
                } catch (requestError) {
                    item?.classList.remove('is-removing');
                    showToast(requestError?.message || 'Nie udało się usunąć produktu.');
                    announce(requestError?.message || 'Nie udało się usunąć produktu.');
                }
            });
        }, reducedMotion.matches ? 0 : 220);
    };

    const restoreItem = (key) => {
        enqueueMutation(async () => {
            try {
                const payload = await request(
                    createCartItemUrl(endpoint, key, 'restore', window.location.href),
                    { method: 'POST', body: {} },
                );
                renderSnapshot(payload, { announceUpdate: true });
                refreshWooFragments();
                showToast('Produkt wrócił do koszyka.');
            } catch (requestError) {
                showToast(requestError?.message || 'Nie udało się przywrócić produktu.');
                announce(requestError?.message || 'Nie udało się przywrócić produktu.');
            }
        });
    };

    toggles.forEach((toggle) => {
        toggle.addEventListener('click', (event) => {
            if (!shouldOpenCartFromClick(event)) {
                return;
            }

            event.preventDefault();
            isOpen() ? close() : open();
        });
    });

    document.addEventListener('click', (event) => {
        if (!shouldOpenCartFromClick(event)) {
            return;
        }

        const target = event.target instanceof Element ? event.target : null;
        const link = target?.closest('a[href]');

        if (
            !link
            || link.matches('[data-cart-toggle]')
            || link.hasAttribute('download')
            || (link.target && link.target !== '_self')
        ) {
            return;
        }

        try {
            const url = new URL(link.href, window.location.href);

            if (
                url.origin !== window.location.origin
                || getCartDrawerCleanUrl(url.href, window.location.origin) === null
            ) {
                return;
            }
        } catch {
            return;
        }

        event.preventDefault();
        open();
    });

    closeButtons.forEach((button) => button.addEventListener('click', () => close()));

    drawer.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;

        if (!target) {
            return;
        }

        const retry = target.closest('[data-cart-retry]');
        const decrease = target.closest('[data-cart-decrease]');
        const increase = target.closest('[data-cart-increase]');
        const remove = target.closest('[data-cart-remove]');

        if (retry) {
            refresh({ force: true });
        } else if (decrease) {
            queueQuantityChange(decrease.dataset.cartDecrease || '', -1);
        } else if (increase) {
            queueQuantityChange(increase.dataset.cartIncrease || '', 1);
        } else if (remove) {
            removeItem(remove.dataset.cartRemove || '');
        }
    });

    toastAction?.addEventListener('click', () => {
        const callback = toastCallback;
        toast.classList.remove('is-visible');
        toast.hidden = true;
        toastCallback = null;
        callback?.();
    });

    document.addEventListener('keydown', (event) => {
        if (!isOpen()) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusable = getFocusableElements(surface);
        const first = focusable[0];
        const last = focusable.at(-1);

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
        }
    });

    const resetDrag = () => {
        drag = null;
        drawer.classList.remove('is-dragging');
        drawer.style.removeProperty('--cart-drawer-drag-x');
    };

    surface.addEventListener('pointerdown', (event) => {
        if (!isOpen() || event.pointerType !== 'touch') {
            return;
        }

        drag = {
            id: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startedAt: performance.now(),
            active: false,
            distance: 0,
        };
    });

    surface.addEventListener('pointermove', (event) => {
        if (!drag || drag.id !== event.pointerId) {
            return;
        }

        const deltaX = event.clientX - drag.startX;
        const deltaY = event.clientY - drag.startY;

        if (!drag.active) {
            if (deltaX < 8 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) {
                return;
            }

            drag.active = true;
            drawer.classList.add('is-dragging');

            try {
                surface.setPointerCapture?.(event.pointerId);
            } catch {
                // The pointer may already be gone (for example after an OS gesture).
            }
        }

        event.preventDefault();
        drag.distance = getDrawerDragOffset(drag.startX, event.clientX, surface.offsetWidth);
        drawer.style.setProperty('--cart-drawer-drag-x', `${drag.distance}px`);
    });

    const finishDrag = (event) => {
        if (!drag || drag.id !== event.pointerId) {
            return;
        }

        const shouldClose = drag.active && shouldCloseFromSwipe({
            distance: drag.distance,
            duration: performance.now() - drag.startedAt,
            width: surface.offsetWidth,
        });

        try {
            if (!surface.hasPointerCapture || surface.hasPointerCapture(event.pointerId)) {
                surface.releasePointerCapture?.(event.pointerId);
            }
        } catch {
            // Losing capture must never leave the drawer in a dragging state.
        }

        resetDrag();

        if (shouldClose) {
            close();
        }
    };

    surface.addEventListener('pointerup', finishDrag);
    surface.addEventListener('pointercancel', finishDrag);

    const handleExternalCartChange = async ({ openDrawer = false, showAov = false, detail = {} } = {}) => {
        lastRefresh = 0;
        const payload = await refresh({ force: true, silent: hasSnapshot });

        if (showAov && payload?.status === 'ready' && presentPostAddOffer(payload, detail)) {
            return;
        }

        if (openDrawer) {
            open({ refreshCart: false });
        }
    };

    if (window.jQuery) {
        const $body = window.jQuery(document.body);
        $body.on('added_to_cart.seluviaCart', (_event, _fragments, _hash, button) => {
            handleExternalCartChange({
                openDrawer: true,
                showAov: true,
                detail: { button: button?.get?.(0) || button?.[0] || null },
            });
        });
        $body.on('removed_from_cart.seluviaCart', () => handleExternalCartChange());
        $body.on('wc_fragments_refreshed.seluviaCart', () => {
            if (Date.now() >= suppressWooRefreshUntil) {
                handleExternalCartChange();
            }
        });
    }

    document.addEventListener('wc-blocks_added_to_cart', (event) => handleExternalCartChange({
        openDrawer: true,
        showAov: true,
        detail: event.detail || {},
    }));
    document.addEventListener('wc-blocks_removed_from_cart', () => handleExternalCartChange());
    document.addEventListener('seluvia:add-to-cart', (event) => {
        const payload = event.detail?.cart;

        if (payload?.status === 'ready') {
            renderSnapshot(payload, { announceUpdate: true });
            refreshWooFragments();

            if (!presentPostAddOffer(payload, event.detail || {})) {
                open({ refreshCart: false });
            }

            return;
        }

        handleExternalCartChange({
            openDrawer: true,
            showAov: true,
            detail: event.detail || {},
        });
    });
    window.addEventListener('online', () => {
        if (isOpen()) {
            handleExternalCartChange();
        }
    });
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden && isOpen() && Date.now() - lastRefresh > 30000) {
            handleExternalCartChange();
        }
    });
    window.addEventListener('pagehide', () => close({ restoreFocus: false }));

    const summaryObserver = typeof ResizeObserver === 'function'
        ? new ResizeObserver(updateSummaryHeight)
        : null;
    summaryObserver?.observe(summary);

    const initialPayload = parseInitialPayload(drawer);

    if (initialPayload?.status === 'ready') {
        renderSnapshot(initialPayload);
    } else {
        renderLoading();
    }

    setInert(drawer, true);
    drawer.dataset.cartReady = 'true';

    const cleanCartUrl = getCartDrawerCleanUrl(window.location.href, window.location.origin);

    if (cleanCartUrl !== null) {
        try {
            window.history.replaceState(window.history.state, '', cleanCartUrl);
        } catch {
            // The drawer still opens if a privacy mode blocks History API changes.
        }

        window.requestAnimationFrame(() => open({ refreshCart: !hasSnapshot }));
    }

    initCartSharing(drawer, {
        endpoint, request, renderSnapshot, refreshWooFragments, open,
        getSnapshot: () => snapshot,
        runTask: (callback) => {
            // Include quantity changes still waiting for their debounce timer.
            quantityTimers.forEach((timer, key) => {
                window.clearTimeout(timer);
                enqueueMutation(() => syncQuantity(key));
            });
            quantityTimers.clear();
            return enqueueMutation(callback);
        },
    });

    return {
        open,
        close,
        isOpen,
        refresh,
        addItem,
        getSnapshot: () => snapshot,
    };
};
